/**
 * 2026 Bǐngwǔ Year — English Deep Narrative Engine
 * 4 Pillars analysis · Seasonal harmony · Stem combinations · Antipathy/Ghost Gate
 */
import type { SajuResult } from './sajuCalculations';
import type { DeepNarrative } from './sajuNarrativeEngine';

// ── 2026 Annual Luck ─────────────────────────────────────────────
const YS = 2; // Bǐng (丙)
const YB = 6; // Wǔ (午)

// ── Branch / Stem arrays ─────────────────────────────────────────
const BEN = ['Zǐ','Chǒu','Yín','Mǎo','Chén','Sì','Wǔ','Wèi','Shēn','Yǒu','Xū','Hài'];
const BCN = ['子','丑','寅','卯','辰','巳','午','未','申','酉','戌','亥'];
const SEN = ['Jiǎ','Yǐ','Bǐng','Dīng','Wù','Jǐ','Gēng','Xīn','Rén','Guǐ'];
const SCN = ['甲','乙','丙','丁','戊','己','庚','辛','壬','癸'];

// ── Element / Ten God logic ──────────────────────────────────────
const EL = [0,0,1,1,2,2,3,3,4,4];
const GEN_EL=[1,2,3,4,0], CTRL=[2,3,4,0,1], GENBY=[4,0,1,2,3], CTRLBY=[3,4,0,1,2];

function sipseong(dayIdx: number, targetIdx: number): string {
  const d=EL[dayIdx],t=EL[targetIdx],dy=dayIdx%2,ty=targetIdx%2;
  if(t===d) return dy===ty?'Companion':'Rob Wealth';
  if(t===GEN_EL[d]) return dy===ty?'Eating God':'Hurting Officer';
  if(t===CTRL[d]) return dy===ty?'Indirect Wealth':'Direct Wealth';
  if(t===CTRLBY[d]) return dy===ty?'7 Killings':'Direct Officer';
  if(t===GENBY[d]) return dy===ty?'Indirect Resource':'Direct Resource';
  return 'Companion';
}

// ── Branch relationships ─────────────────────────────────────────
type BRel = '충'|'삼합'|'방합'|'육합'|'형'|'자형'|'파'|'해'|'원진'|'귀문'|'없음';

function branchRels(a: number, b: number): BRel[] {
  const rels: BRel[] = [];
  if((a+6)%12===b||(b+6)%12===a) rels.push('충');
  const yh:number[][] = [[0,1],[2,11],[3,10],[4,9],[5,8],[6,7]];
  if(yh.some(p=>(p[0]===a&&p[1]===b)||(p[1]===a&&p[0]===b))) rels.push('육합');
  const sh:number[][] = [[2,6,10],[11,3,7],[5,9,1],[8,0,4]];
  if(sh.some(g=>g.includes(a)&&g.includes(b)&&a!==b)) rels.push('삼합');
  const fh:number[][] = [[2,3,4],[5,6,7],[8,9,10],[11,0,1]];
  if(fh.some(g=>g.includes(a)&&g.includes(b)&&a!==b)) rels.push('방합');
  const hy:number[][] = [[2,5],[5,8],[8,2],[1,10],[10,7],[7,1],[0,3],[3,0]];
  if(hy.some(p=>p[0]===a&&p[1]===b)) rels.push('형');
  if(a===b&&[4,6,9,11].includes(a)) rels.push('자형');
  const pa:number[][] = [[0,9],[9,0],[6,3],[3,6],[4,1],[1,4],[7,10],[10,7],[2,11],[11,2],[5,8],[8,5]];
  if(pa.some(p=>p[0]===a&&p[1]===b)) rels.push('파');
  const hae:number[][] = [[0,7],[7,0],[1,6],[6,1],[2,5],[5,2],[3,4],[4,3],[8,11],[11,8],[9,10],[10,9]];
  if(hae.some(p=>p[0]===a&&p[1]===b)) rels.push('해');
  const wj:number[][] = [[0,7],[7,0],[1,6],[6,1],[2,9],[9,2],[3,8],[8,3],[4,11],[11,4],[5,10],[10,5]];
  if(wj.some(p=>p[0]===a&&p[1]===b)) rels.push('원진');
  const gm:number[][] = [[9,0],[0,9],[6,1],[1,6],[7,2],[2,7],[8,3],[3,8],[11,4],[4,11],[10,5],[5,10]];
  if(gm.some(p=>p[0]===a&&p[1]===b)) rels.push('귀문');
  return rels.length ? rels : ['없음'];
}

function relNameEn(a: number, b: number, rels: BRel[]): string {
  const an=BCN[a], bn=BCN[b], ae=BEN[a], be=BEN[b];
  return rels.filter(r=>r!=='없음').map(r=>`${an}${bn} ${
    r==='충'?`Clash(${ae}-${be})`:
    r==='삼합'?`Triple Harmony`:
    r==='육합'?`Six Harmony`:
    r==='방합'?`Directional Harmony`:
    r==='형'?`Punishment`:
    r==='자형'?`Self-Punishment`:
    r==='파'?`Breach(破)`:
    r==='해'?`Harm(害)`:
    r==='원진'?`Antipathy`:
    `Ghost Gate`
  }`).join(' · ');
}

function stemCombEn(a: number, b: number): string|null {
  if((b-a+10)%10===5||(a-b+10)%10===5) {
    const p = Math.min(a,b);
    const map:Record<number,string> = {
      0:`${SCN[0]}${SCN[5]} Stem Combo → Earth`,
      1:`${SCN[1]}${SCN[6]} Stem Combo → Metal`,
      2:`${SCN[2]}${SCN[7]} Stem Combo → Water`,
      3:`${SCN[3]}${SCN[8]} Stem Combo → Wood`,
      4:`${SCN[4]}${SCN[9]} Stem Combo → Fire`,
    };
    return map[p%5]||null;
  }
  return null;
}

// ── Day Master metaphors (English) ───────────────────────────────
const METAPHOR_EN: Record<number,{label:string;metaphor:string;nature:string}> = {
  0:{label:'Jiǎmù (甲木)',metaphor:'a towering mountain pine',nature:'unstoppable upward life force'},
  1:{label:'Yǐmù (乙木)',metaphor:'resilient vines and grass',nature:'tenacious adaptability that always survives'},
  2:{label:'Bǐnghuǒ (丙火)',metaphor:'the blazing sun itself',nature:'the pure essence of yang that illuminates all'},
  3:{label:'Dīnghuǒ (丁火)',metaphor:'a steadfast candle in the dark',nature:'subtle yet precious, irreplaceable light'},
  4:{label:'Wùtǔ (戊土)',metaphor:'a great mountain and the earth',nature:'immovable foundation that quietly supports everything'},
  5:{label:'Jǐtǔ (己土)',metaphor:'fertile farmland nurturing all life',nature:'warm, patient earth that cultivates results'},
  6:{label:'Gēngjīn (庚金)',metaphor:'raw ore being forged into a blade',nature:'strength refined through hardship into lasting value'},
  7:{label:'Xīnjīn (辛金)',metaphor:'a polished jewel catching the light',nature:'sharp, refined brilliance that stands out'},
  8:{label:'Rénshǔi (壬水)',metaphor:'a vast, deep ocean',nature:'boundless wisdom that embraces all'},
  9:{label:'Guǐshǔi (癸水)',metaphor:'life-giving rain',nature:'quiet yet profound wisdom and depth'},
};

// ── Ten God × Day Master narrative ───────────────────────────────
const SIP_META_EN: Record<string,string> = {
  'Companion':        'Two suns rising in the same sky — expect rivalry and collaboration to intertwine all year.',
  'Rob Wealth':       'A blazing sun invades the candle\'s domain — your resources and territory face real pressure.',
  'Eating God':       'Your inner energy blooms creatively — talents flourish and opportunities flow naturally.',
  'Hurting Officer':  'Strong sunlight can scorch or strengthen growth. This is a double-edged year that rewards the bold.',
  'Indirect Wealth':  'Sunlight shatters across the waves, revealing hidden value — a year of unexpected wealth opportunities.',
  'Direct Wealth':    'The sun illuminates the fields — honest, steady effort bears tangible, lasting fruit.',
  '7 Killings':       'Raging fire tempers the ore — intense pressure now becomes the launching pad for a breakthrough.',
  'Direct Officer':   'Sunlight makes the jewel dazzle — recognition, status, and credibility follow principled action.',
  'Indirect Resource':'The sun warms the mountain, awakening deep intuition and inner wisdom.',
  'Direct Resource':  'Sunlight nurtures the farmland — mentors, benefactors, and supporters appear when you need them.',
};

// ── Seasonal (調候) analysis (English) ───────────────────────────
function chohuTextEn(monthBranch: number, dayStemIdx: number): string {
  const season = [3,3,0,0,0,1,1,1,2,2,2,3][monthBranch];
  const bn = BEN[monthBranch];
  if(season===3) return `Born in a winter month (${bn}), the fire energy of 2026's Bǐngwǔ year is far more than a yearly influence — it acts as a warming remedy (調候) for your chart. It is simultaneously a threat and a salvation, a duality that defines your entire 2026.`;
  if(season===1) return `Born in a summer month (${bn}), Bǐngwǔ's fierce fire piles yet more heat onto an already scorching chart. Overheating is the primary risk. Metal and Water energies are this year's essential antidote — seek them actively.`;
  if(season===2) return `Born in an autumn month (${bn}), Bǐngwǔ's fire challenges your chart's Metal energy through Fire-controls-Metal (火剋金). Structures that felt solid may now face unexpected pressure and testing.`;
  return `Born in a spring month (${bn}), Bǐngwǔ's fire ignites the Wood energy already in your chart, doubling the growth momentum. The combination of Wood feeding Fire creates powerful expansion energy.`;
}

// ── Position governs ─────────────────────────────────────────────
const GW_EN: Record<'년'|'월'|'일'|'시',string> = {
  년:'ancestral roots, early life, and social heritage',
  월:'parents, siblings, career, and social life',
  일:'spouse, self, and current environment',
  시:'children, old age, subordinates, and the future',
};
const POS_EN: Record<'년'|'월'|'일'|'시',string> = {
  년:'Year', 월:'Month', 일:'Day', 시:'Hour',
};

// ── Pillar relationship narrative (English) ──────────────────────
function pillarRelTextEn(pos: '년'|'월'|'일'|'시', branchEn: string, branchCn: string, relList: BRel[], relStr: string): string {
  const gw = GW_EN[pos];
  const posEn = POS_EN[pos];
  if(relList.includes('없음')||relList.length===0) return '';
  const hasChung = relList.includes('충');
  const hasHap = relList.includes('삼합')||relList.includes('방합')||relList.includes('육합');
  const hasPa = relList.includes('파');
  const hasHae = relList.includes('해');
  const hasWj = relList.includes('원진');
  const hasGm = relList.includes('귀문');
  if(hasChung) return `Your ${posEn} Pillar (${branchCn}) forms a **${relStr}** with the 2026 annual branch Wǔ. The domain of ${gw} receives a direct energetic shock. A Clash (冲) doesn't mean disaster — it forces transformation. Meet this upheaval with decisive action rather than fear.`;
  if(hasHap) return `Your ${posEn} Pillar (${branchCn}) forms a **${relStr}** with the 2026 annual branch Wǔ. The energy governing ${gw} merges with the annual fire, amplifying it significantly. Harness this powerful convergence for a major leap forward.`;
  if(hasPa) return `Your ${posEn} Pillar (${branchCn}) forms a **${relStr}** with the 2026 annual branch Wǔ. A Breach (破) creates invisible fractures that build slowly. Watch for unexpected variables in the domain of ${gw} — proactive checks prevent larger issues.`;
  if(hasWj && hasGm) return `Your ${posEn} Pillar (${branchCn}) forms a **${relStr}** with the 2026 annual branch Wǔ. The overlap of Antipathy (怨嗔) and Ghost Gate (鬼門) in the domain of ${gw} creates mental fatigue and internal conflict. Insomnia, anxiety, and unexplained friction with close people are classic symptoms. Prioritize mental health this year.`;
  if(hasHae||hasWj) return `Your ${posEn} Pillar (${branchCn}) forms a **${relStr}** with the 2026 annual branch Wǔ. Hidden obstacles, misunderstandings, and subtle sabotage can emerge in the domain of ${gw}. Stay alert to the undercurrents.`;
  return `Your ${posEn} Pillar (${branchCn}) forms ${relStr} with the 2026 annual branch Wǔ.`;
}

// ── Monthly data ─────────────────────────────────────────────────
interface MonthNarrativeEn {
  month: number; solarMonth: string; ganji: string; score: number;
  title: string; detail: string; tags: string[];
}

const MONTHS_2026_EN = [
  {m:1,sm:'Feb',ganji:'Gēngyín (庚寅)',si:6,bi:2},
  {m:2,sm:'Mar',ganji:'Xīnmǎo (辛卯)',si:7,bi:3},
  {m:3,sm:'Apr',ganji:'Rénchén (壬辰)',si:8,bi:4},
  {m:4,sm:'May',ganji:'Guǐsì (癸巳)',si:9,bi:5},
  {m:5,sm:'Jun',ganji:'Jiǎwǔ (甲午)',si:0,bi:6},
  {m:6,sm:'Jul',ganji:'Yǐwèi (乙未)',si:1,bi:7},
  {m:7,sm:'Aug',ganji:'Bǐngshēn (丙申)',si:2,bi:8},
  {m:8,sm:'Sep',ganji:'Dīngyǒu (丁酉)',si:3,bi:9},
  {m:9,sm:'Oct',ganji:'Wùxū (戊戌)',si:4,bi:10},
  {m:10,sm:'Nov',ganji:'Jǐhài (己亥)',si:5,bi:11},
  {m:11,sm:'Dec',ganji:'Gēngzǐ (庚子)',si:6,bi:0},
  {m:12,sm:"Jan '27",ganji:'Xīnchǒu (辛丑)',si:7,bi:1},
];

function genMonthNarrativeEn(mData: typeof MONTHS_2026_EN[0], result: SajuResult): MonthNarrativeEn {
  const {m,sm,ganji,si,bi} = mData;
  const dayS = result.dayPillar.stemIdx, dayB = result.dayPillar.branchIdx;
  const monB = result.monthPillar.branchIdx;
  const yrB = result.yearPillar.branchIdx;
  const hrB = result.hourPillar?.branchIdx;

  const sip = sipseong(dayS, si);
  const tComb = stemCombEn(dayS, si);

  const interactions: Array<{pos:string; rels:BRel[]; rStr:string}> = [];
  const checkList: Array<[string, number]> = [
    ['Annual Wǔ', YB], ['Year Branch', yrB], ['Month Branch', monB], ['Day Branch', dayB],
  ];
  if(hrB !== undefined) checkList.push(['Hour Branch', hrB]);

  for(const [posLabel, origB] of checkList) {
    if(bi === origB && posLabel !== 'Annual Wǔ') continue;
    const rels = branchRels(bi, origB);
    if(!rels.includes('없음')) {
      const rStr = relNameEn(bi, origB, rels);
      interactions.push({pos: posLabel, rels, rStr});
    }
  }

  let score = 62;
  for(const {rels} of interactions) {
    if(rels.includes('삼합')||rels.includes('육합')) score += 10;
    if(rels.includes('방합')) score += 6;
    if(rels.includes('충')) score -= 12;
    if(rels.includes('형')||rels.includes('자형')) score -= 7;
    if(rels.includes('파')) score -= 5;
    if(rels.includes('해')||rels.includes('원진')) score -= 5;
    if(rels.includes('귀문')) score -= 4;
  }
  const goodSip = ['Eating God','Direct Wealth','Direct Resource','Indirect Wealth','Direct Officer'];
  const badSip = ['7 Killings','Rob Wealth','Hurting Officer'];
  if(goodSip.includes(sip)) score += 6;
  if(badSip.includes(sip)) score -= 6;
  if(tComb) score += 8;
  score = Math.max(28, Math.min(96, score));

  const tags: string[] = [sip];
  for(const {rStr} of interactions) tags.push(rStr);
  if(tComb) tags.push(tComb);

  const criticalRels = interactions.filter(i=>i.rels.includes('충')||i.rels.includes('형'));
  const goodRels = interactions.filter(i=>i.rels.includes('삼합')||i.rels.includes('육합'));
  const mBCN = BCN[bi];

  let title = '', detail = '';

  if(tComb) {
    title = `${ganji} · ${tComb} — A Benefactor Arrives`;
    detail = `In ${sm} (${ganji}), the Heavenly Stem forms a Stem Combination (${tComb}) with your Day Master. When this occurs, a meaningful ally or benefactor tends to appear — someone who recognizes your value and opens doors. Positive turning points emerge in career, business, and relationships. However, watch the Earthly Branch level carefully: ${interactions.length ? interactions.map(i=>i.rStr).join(', ') + ' also apply.' : 'the branches are relatively calm.'}`;
    return {month:m,solarMonth:sm,ganji,score:Math.min(96,score+5),title,detail,tags};
  }

  if(criticalRels.length) {
    const clashDesc = criticalRels.map(i=>`${i.pos}: ${i.rStr}`).join(', ');
    title = `${ganji} · ${criticalRels.map(i=>i.rStr).join(' ')} — A Month of Major Shifts`;
    detail = `${sm} (${ganji}) is one of the most intense months of the year. The ${mBCN} branch entering this month triggers ${clashDesc}. `;
    if(criticalRels.some(i=>i.pos.includes('Month'))) detail += `Your Month Pillar — governing career and social life — takes a direct hit. Expect role changes, career shifts, or sudden restructuring at work. `;
    if(criticalRels.some(i=>i.pos.includes('Day'))) detail += `Your Day Pillar — representing yourself and your immediate environment — is also shaken. Monitor your health and key relationships closely. `;
    detail += `Remember: a Clash isn't inherently bad. It forces transformation. Ride the wave decisively rather than resisting it.`;
    if(goodRels.length) detail += ` Notably, ${goodRels.map(i=>i.rStr).join(', ')} also operate simultaneously, providing momentum to support the changes.`;
    return {month:m,solarMonth:sm,ganji,score,title,detail,tags};
  }

  if(goodRels.length) {
    const hapStr = goodRels.map(i=>i.rStr).join(', ');
    title = `${ganji} · ${hapStr} — Energy Converges Powerfully`;
    detail = `In ${sm} (${ganji}), a Harmony (${hapStr}) forms, pulling energies into a potent convergence. This is an excellent window for new connections, contracts, and partnerships to take shape. What you've been building starts to materialize into concrete results.`;
    if(goodRels.some(i=>i.rels.includes('삼합'))) detail += ` The Triple Harmony in particular amplifies the fire energy of this entire year into a focused peak.`;
    return {month:m,solarMonth:sm,ganji,score,title,detail,tags};
  }

  const softNeg = interactions.filter(i=>i.rels.some(r=>['파','해','원진','귀문'].includes(r)));
  if(softNeg.length) {
    const relStr = softNeg.map(i=>i.rStr).join(', ');
    title = `${ganji} · ${relStr} — Watch for Hidden Friction`;
    detail = `${sm} (${ganji}) carries ${relStr}. Less dramatic than a Clash, but slow-building tension emerges beneath the surface. Outwardly calm, yet internal conflict, unexpected expenses, and misunderstandings with others tend to surface. Consolidate rather than expand — protect what you have.`;
    return {month:m,solarMonth:sm,ganji,score,title,detail,tags};
  }

  const mood = score >= 65 ? 'Smooth' : score >= 50 ? 'Steady' : 'Quiet';
  title = `${ganji} · ${sip} — A ${mood} Month`;
  detail = `${sm}'s Heavenly Stem acts as ${sip} for your Day Master. ${
    goodSip.includes(sip)
      ? 'Your abilities surface naturally and consistent effort accumulates reward. Let steady progress be the theme rather than dramatic moves.'
      : badSip.includes(sip)
      ? 'Energy can feel overextended and competition heats up. Protect your footing and build internal strength rather than pushing outward.'
      : 'A relatively uneventful month — ideal for mapping out longer-term plans and preparing for what\'s ahead.'
  }`;
  return {month:m,solarMonth:sm,ganji,score,title,detail,tags};
}

// ── Main English narrative generator ────────────────────────────
export function generateDeepNarrativeEn(result: SajuResult): DeepNarrative {
  const dayS = result.dayPillar.stemIdx;
  const dayB = result.dayPillar.branchIdx;
  const monS = result.monthPillar.stemIdx;
  const monB = result.monthPillar.branchIdx;
  const yrS = result.yearPillar.stemIdx;
  const yrB = result.yearPillar.branchIdx;
  const hrS = result.hourPillar?.stemIdx;
  const hrB = result.hourPillar?.branchIdx;

  const meta = METAPHOR_EN[dayS];
  const sip = sipseong(dayS, YS);
  const sipM = SIP_META_EN[sip] ?? '';

  const pillarData: Array<{pos:'년'|'월'|'일'|'시'; branchIdx:number; pillarStr:string}> = [
    {pos:'년', branchIdx:yrB, pillarStr:`${SCN[yrS]}${BCN[yrB]}`},
    {pos:'월', branchIdx:monB, pillarStr:`${SCN[monS]}${BCN[monB]}`},
    {pos:'일', branchIdx:dayB, pillarStr:`${SCN[dayS]}${BCN[dayB]}`},
    ...(hrB!==undefined&&hrS!==undefined ? [{pos:'시' as const, branchIdx:hrB, pillarStr:`${SCN[hrS]}${BCN[hrB]}`}] : []),
  ];

  const pillarAnalysis = pillarData.map(({pos, branchIdx, pillarStr}) => {
    const rels = branchRels(branchIdx, YB);
    const rStr = relNameEn(branchIdx, YB, rels);
    const text = rels.includes('없음') ? '' : pillarRelTextEn(pos, BEN[branchIdx], BCN[branchIdx], rels, rStr);
    return {pos, label:`${POS_EN[pos]} Pillar (${pillarStr})`, pillarStr, rels, relStr:rStr, text};
  }).filter(p=>!p.rels.includes('없음'));

  let score = 60;
  const sipScores: Record<string,number> = {
    'Eating God':82,'Direct Wealth':80,'Direct Resource':78,'Direct Officer':75,
    'Indirect Wealth':76,'Indirect Resource':70,'Companion':65,'Hurting Officer':62,'Rob Wealth':56,'7 Killings':54,
  };
  score = sipScores[sip] || 65;
  for(const p of pillarAnalysis) {
    if(p.rels.includes('삼합')||p.rels.includes('육합')) score += 7;
    if(p.rels.includes('방합')) score += 4;
    if(p.rels.includes('충')) score -= (p.pos==='월'?10:7);
    if(p.rels.includes('파')||p.rels.includes('해')) score -= 4;
    if(p.rels.includes('원진')&&p.rels.includes('귀문')) score -= 8;
  }
  score = Math.max(30, Math.min(95, score));

  const dayCN = `${result.dayPillar.stem}${result.dayPillar.branch}`;
  const mainChungs = pillarAnalysis.filter(p=>p.rels.includes('충'));
  const mainHaps = pillarAnalysis.filter(p=>p.rels.includes('삼합')||p.rels.includes('육합')||p.rels.includes('방합'));

  const p1 = `As the ${meta.label} — ${meta.metaphor} — your 2026 Bǐngwǔ year (丙午年) is anything but ordinary. The annual Heavenly Stem Bǐnghuǒ (丙火) arrives as your **${sip}** energy. ${sipM}`;
  const p2 = chohuTextEn(monB, dayS);
  const chungDesc = mainChungs.length
    ? `Pay particular attention to ${mainChungs.map(p=>`your ${POS_EN[p.pos]} Pillar (${p.relStr})`).join(' and ')}. ${mainChungs.map(p=>`The ${POS_EN[p.pos]} Pillar — governing ${GW_EN[p.pos]} — absorbs a direct shock from the annual energy.`).join(' ')}`
    : '';
  const hapDesc = mainHaps.length ? ` Counterbalancing this, ${mainHaps.map(p=>`${p.pos === '년'?'Year':p.pos === '월'?'Month':p.pos === '일'?'Day':'Hour'} Pillar ${p.relStr}`).join(' and ')} also operate as favorable influences.` : '';
  const p3 = (chungDesc + hapDesc) || `Your 4 Pillars show no major clashes with the 2026 annual branch — a relatively stable and manageable year overall.`;

  const strategy = ['Rob Wealth','7 Killings'].includes(sip)
    ? `2026 is a year for offense, not defense. Don't try to overpower the competitive pressure — ride it instead. Document your achievements meticulously, promote yourself proactively, and claim credit for your work. Staying invisible is the only real failure this year.`
    : ['Eating God','Indirect Wealth','Direct Wealth'].includes(sip)
    ? `Don't hide your talents in 2026 — the more you express and share them, the more opportunities flow toward you. When money comes in, immediately earmark a portion into locked, safe assets. Your biggest risk this year is letting prosperity slip through your fingers.`
    : ['Direct Officer','Direct Resource'].includes(sip)
    ? `Integrity and principle are your fastest routes forward in 2026. Benefactors are near — stay open to offers and propositions from those around you. Trust the process and avoid shortcuts.`
    : `2026 calls for building depth over chasing breadth. Protect what you have, refine what you're building, and wait for the right moment. Patient preparation now leads to significant returns ahead.`;

  const monthPillarRel = pillarAnalysis.find(p=>p.pos==='월');
  const dayPillarRel = pillarAnalysis.find(p=>p.pos==='일');
  const hourPillarRel = pillarAnalysis.find(p=>p.pos==='시');

  const careerText = monthPillarRel
    ? `Your Month Pillar (${monthPillarRel.pillarStr}) — which governs career and social life — forms ${monthPillarRel.relStr} with the 2026 annual branch. ${
        monthPillarRel.rels.includes('충')
          ? `A Clash striking the Month Pillar signals unavoidable career disruption: department reshuffles, job changes, or launching out on your own. Don't fear it — this energy is the catalyst for a leap forward. Record your contributions and advocate for yourself aggressively.`
          : (monthPillarRel.rels.includes('삼합')||monthPillarRel.rels.includes('육합'))
          ? `A Harmony in the Month Pillar amplifies your professional energy. New opportunities, contracts, and partnerships arrive organically — this is a year to say yes and move boldly.`
          : `Subtle shifts and friction in your professional environment are possible. Stay proactive and don't let small tensions compound.`
      }`
    : `Under ${sip} energy, your career flows ${['Eating God','Direct Officer','Direct Resource'].includes(sip) ? 'strongly — your talents will be recognized and results formalized.' : ['Rob Wealth','7 Killings'].includes(sip) ? 'under pressure — competition intensifies. But strong charts thrive under this pressure.' : 'steadily — consistent effort builds meaningful results.'}`;

  const moneyText = dayPillarRel
    ? `Your Day Branch (${result.dayPillar.branch}) — the real storehouse of wealth — forms ${dayPillarRel.relStr} with the 2026 annual branch. ${
        dayPillarRel.rels.includes('파')
          ? `A Breach (破) hitting the wealth storehouse means money comes in, but leaks out just as fast. Business proposals from acquaintances, lending money to friends, and impulsive investments are the chief culprits. Lock a portion of every income stream into an account you can't easily touch — property, fixed deposits, or similar.`
          : dayPillarRel.rels.includes('충')
          ? `A Clash on the Day Branch creates volatile wealth swings. Large unexpected income — or equally large surprise expenses — are both possible. Tighten your financial controls significantly this year.`
          : `The Day Branch carries ${dayPillarRel.relStr} energy. Monitor your cash flow and maintain disciplined asset management throughout.`
      }`
    : `Under ${sip} energy, wealth flows ${['Indirect Wealth','Direct Wealth'].includes(sip) ? 'favorably, though income tends to match outflow. Saving discipline is essential.' : ['Rob Wealth','Hurting Officer'].includes(sip) ? 'cautiously — unexpected expenses and competition for resources are likely.' : 'steadily with consistent management.'}`;

  const healthText = hourPillarRel
    ? `Your Hour Pillar (${hourPillarRel.pillarStr}) — governing home life, inner world, and the future — forms ${hourPillarRel.relStr} with the 2026 annual branch. ${
        (hourPillarRel.rels.includes('원진')&&hourPillarRel.rels.includes('귀문'))
          ? `The combination of Antipathy (怨嗔) and Ghost Gate (鬼門) here is a serious mental health warning. Your mind will feel overloaded — insomnia, anxiety, and heightened emotional sensitivity are the signature symptoms. Actively monitor cardiovascular health and blood pressure. Schedule "empty time" weekly, and use cooling activities like swimming or warm baths to discharge the excess heat energy.`
          : `Shifts in the Hour Pillar domain affect home stability and inner equilibrium. Avoid burnout by building intentional rest into your schedule.`
      }`
    : `2026's strong fire energy puts cardiovascular health, blood pressure, and eye health in the spotlight. Feeling energized is not an excuse to neglect recovery — sudden burnout follows unchecked intensity.`;

  const loveText = (() => {
    const isDohwa = [0,3,6,9].includes(dayB) || [0,3,6,9].includes(YB);
    const base = isDohwa
      ? `Your Day Branch (${BEN[dayB]}) or the 2026 annual branch Wǔ falls within the Peach Blossom (桃花) group (Zǐ/Wǔ/Mǎo/Yǒu). When Peach Blossom activates, your charisma intensifies and your social circle widens — but emotional drain and relationship complications can follow. Depth over breadth: invest in the people who truly matter.`
      : '';
    if(dayPillarRel?.rels.includes('파')) return base + ` A Breach (破) on your Day Branch unsettles the marriage/partnership palace. Frequent misunderstandings and friction with your partner are common. Practice deliberate pauses before reacting — one breath can save many arguments.`;
    if(dayPillarRel?.rels.includes('육합')||dayPillarRel?.rels.includes('삼합')) return base + ` A Harmony forms in your Day Branch — this is a powerful year for new romantic commitments or deepening existing ones. If you've been considering marriage, moving in together, or a serious commitment, 2026 is highly favorable timing.`;
    return base || `Relationships flow ${['Rob Wealth','Hurting Officer'].includes(sip) ? 'actively but with tension — watch your words, as bluntness can leave lasting damage.' : 'generally smoothly this year.'}`;
  })();

  const months = MONTHS_2026_EN.map(mData => genMonthNarrativeEn(mData, result));

  const pillarsStr = [
    result.yearPillar.stem + result.yearPillar.branch,
    result.monthPillar.stem + result.monthPillar.branch,
    result.dayPillar.stem + result.dayPillar.branch,
    result.hourPillar ? result.hourPillar.stem + result.hourPillar.branch : '',
  ].filter(Boolean).join(' ');

  return {
    reportTitle: `2026 Bǐngwǔ Year (丙午年) — In-Depth Destiny Analysis Report`,
    pillarsStr,
    dayPillarLabel: `${result.dayPillar.stem}${result.dayPillar.branch} Day Pillar (${meta.label})`,
    overallScore: score,
    seunSipseong: sip,
    overviewTitle: `"${dayCN} Day Pillar Meets Bǐngwǔ Year (丙午年)"`,
    overviewP1: p1,
    overviewP2: p2,
    overviewP3: p3,
    strategy,
    pillarAnalysis,
    careerText,
    moneyText,
    healthText,
    loveText,
    months,
  };
}
